import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PremiumBadge } from '../PremiumGate';
import OpenAtFilter from './OpenAtFilter';

function ListDetailTabs({ activeTab, isOwnList, isUserCollaborator, onTabChange, places, totalPlaceCount, userPlacesMap, getDesignation }) {
  // Show Been/Wishlist tabs for own lists OR collaboration lists where user is a collaborator
  // Show Listed/Unlisted tabs for other users' lists (followed/viewed)
  const showUserDesignationTabs = isOwnList || isUserCollaborator;

  // Helper to get badge from user's global designation
  const getBadge = (place) => {
    if (!place.placeId || !getDesignation) return null;
    const designation = getDesignation(place.placeId);
    return designation?.badge || null;
  };

  // Calculate counts for each tab. The All count uses the list's total when
  // provided so it stays stable while item pages are still streaming in.
  const allCount = totalPlaceCount ?? (places?.length || 0);

  const beenCount = places?.filter(place => {
    const badge = getBadge(place);
    return badge === 'visited' ||
      badge === 'gem' ||
      badge === 'highly-recommended' ||
      badge === 'avoid';
  }).length || 0;

  const wishlistCount = places?.filter(place => getBadge(place) === 'want-to-go').length || 0;

  const listedCount = places?.filter(place => {
    const placeInfo = userPlacesMap?.[place.placeId];
    return placeInfo && placeInfo.length > 0;
  }).length || 0;

  const unlistedCount = places?.filter(place => {
    const placeInfo = userPlacesMap?.[place.placeId];
    return !placeInfo || placeInfo.length === 0;
  }).length || 0;

  return (
    <div className="tabs">
      <button
        className={`tab ${activeTab === 'all' ? 'active' : ''}`}
        onClick={() => onTabChange('all')}
      >
        All ({allCount})
      </button>
      {showUserDesignationTabs ? (
        <>
          <button
            className={`tab ${activeTab === 'been' ? 'active' : ''}`}
            onClick={() => onTabChange('been')}
          >
            Been ({beenCount})
          </button>
          <button
            className={`tab ${activeTab === 'want-to-go' ? 'active' : ''}`}
            onClick={() => onTabChange('want-to-go')}
          >
            Wishlist ({wishlistCount})
          </button>
        </>
      ) : (
        <>
          <button
            className={`tab ${activeTab === 'listed' ? 'active' : ''}`}
            onClick={() => onTabChange('listed')}
          >
            Listed ({listedCount})
          </button>
          <button
            className={`tab ${activeTab === 'unlisted' ? 'active' : ''}`}
            onClick={() => onTabChange('unlisted')}
          >
            Unlisted ({unlistedCount})
          </button>
        </>
      )}
    </div>
  );
}

function SearchFilterBar({
  searchQuery,
  onSearchChange,
  sortOption,
  onSortChange,
  showSortDropdown,
  onToggleSortDropdown,
  sortDropdownRef,
  userLocation,
  canSortByOpen,
  // Open At filter props
  canFilterByOpenAt,
  openAtFilter,
  onOpenAtFilterChange,
  onClearOpenAtFilter,
  openAtMatchCount,
}) {
  const navigate = useNavigate();
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchInputRef = useRef(null);

  // Check if Open At filter is active
  const isOpenAtActive = openAtFilter && (openAtFilter.days.length > 0 && openAtFilter.slots.length > 0);

  const handleClearSearch = () => {
    onSearchChange('');
    searchInputRef.current?.focus();
  };

  const handleSearchFocus = () => {
    setIsSearchFocused(true);
  };

  const handleSearchBlur = () => {
    // Small delay to allow click events on clear button
    setTimeout(() => {
      if (!searchQuery) {
        setIsSearchFocused(false);
      }
    }, 100);
  };

  // Close search expansion when search is cleared and loses focus
  useEffect(() => {
    if (!searchQuery && !isSearchFocused) {
      setIsSearchFocused(false);
    }
  }, [searchQuery, isSearchFocused]);

  return (
    <div className={`search-filter-bar ${isSearchFocused ? 'search-expanded' : ''}`}>
      <div className="search-bar">
        <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="11" cy="11" r="8"/>
          <path d="m21 21-4.35-4.35"/>
        </svg>
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search this list..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          onFocus={handleSearchFocus}
          onBlur={handleSearchBlur}
          aria-label="Search this list"
        />
        {searchQuery && (
          <button
            className="search-clear-btn"
            onClick={handleClearSearch}
            aria-label="Clear search"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        )}
      </div>

      {/* Open At Filter - hidden when search is focused */}
      <div className={`filter-buttons ${isSearchFocused ? 'hidden' : ''}`}>
        <OpenAtFilter
          selectedDays={openAtFilter?.days || []}
          selectedSlots={openAtFilter?.slots || []}
          onFilterChange={(days, slots) => onOpenAtFilterChange({ days, slots })}
          onClearFilter={onClearOpenAtFilter}
          canFilterByOpenAt={canFilterByOpenAt}
          matchCount={openAtMatchCount}
          isActive={isOpenAtActive}
        />

        <div className="sort-dropdown-wrapper" ref={sortDropdownRef}>
          <button
            className={`sort-dropdown-trigger ${sortOption !== 'default' ? 'active' : ''}`}
            onClick={onToggleSortDropdown}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M3 6h18M6 12h12M9 18h6"/>
            </svg>
            {sortOption === 'default' ? 'Sort' :
             sortOption === 'nearest' ? 'Nearest' :
             sortOption === 'category' ? 'Category' :
             sortOption === 'open' ? 'Open Now' : 'Sort'}
          </button>
          {showSortDropdown && (
            <div className="sort-dropdown-menu">
              <button
                className={`sort-option ${sortOption === 'default' ? 'active' : ''}`}
                onClick={() => onSortChange('default')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                </svg>
                Default (by rating)
              </button>
              <button
                className={`sort-option ${sortOption === 'nearest' ? 'active' : ''}`}
                onClick={() => onSortChange('nearest')}
                disabled={!userLocation}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                Nearest first
                {!userLocation && <span className="option-note">(location needed)</span>}
              </button>
              <button
                className={`sort-option ${sortOption === 'category' ? 'active' : ''}`}
                onClick={() => onSortChange('category')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="3" y="3" width="7" height="7"/>
                  <rect x="14" y="3" width="7" height="7"/>
                  <rect x="3" y="14" width="7" height="7"/>
                  <rect x="14" y="14" width="7" height="7"/>
                </svg>
                By category
              </button>
              <button
                className={`sort-option ${sortOption === 'open' ? 'active' : ''} ${!canSortByOpen ? 'premium-locked' : ''}`}
                onClick={() => {
                  if (canSortByOpen) {
                    onSortChange('open');
                  } else {
                    navigate('/settings/premium');
                  }
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                Open now only
                {!canSortByOpen && <PremiumBadge small />}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { ListDetailTabs, SearchFilterBar };
